import { useEffect, useMemo, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, Search, Loader2, Save, RotateCcw, Lock, Info, ShieldCheck, Minus, Copy,
} from "lucide-react";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** Green-on / grey-off access toggle (precise control + indeterminate support for modules). */
function AccessToggle({ checked, indeterminate, disabled, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={indeterminate ? "mixed" : checked}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!checked); }}
      data-testid={testid}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed ${
        indeterminate ? "bg-amber-400" : checked ? "bg-emerald-600" : "bg-transparent border border-border"
      }`}
    >
      <span
        className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white shadow transition-transform duration-200 ${
          indeterminate ? "translate-x-[10px]" : checked ? "translate-x-[18px]" : "translate-x-[3px]"
        }`}
      >
        {indeterminate && <Minus className="h-2.5 w-2.5 text-amber-600" />}
      </span>
    </button>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2" data-testid="pm-skeleton">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-12 rounded-lg bg-muted/60 animate-pulse" />
      ))}
    </div>
  );
}

/**
 * Menu-Driven Permission Manager.
 * props: roleId, roleCode, isSuperAdmin (role-level), onSaved (cb), roles (for clone)
 */
export default function PermissionManager({ roleId, roleCode, isSuperAdmin, onSaved, roles = [] }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [modules, setModules] = useState([]); // [{module, menus:[...]}]
  const [legacyCodes, setLegacyCodes] = useState([]);
  const [initial, setInitial] = useState(() => new Set());
  const [granted, setGranted] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [reg, perms] = await Promise.all([
        api.get("/v1/menu-registry"),
        api.get(`/v1/roles/${roleId}/permissions`),
      ]);
      const mods = reg.data.data.modules || [];
      setModules(mods);
      const grantedCodes = new Set(
        (perms.data.data.menus || []).filter((m) => m.granted).map((m) => m.permission_code)
      );
      setLegacyCodes(perms.data.data.legacyCodes || []);
      setInitial(new Set(grantedCodes));
      setGranted(new Set(grantedCodes));
      setExpanded(new Set(mods.length ? [mods[0].module] : []));
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load]);

  const allCodes = useMemo(
    () => modules.flatMap((m) => m.menus.map((x) => x.permission_code)),
    [modules]
  );
  const totalMenus = allCodes.length;
  const grantedCount = useMemo(
    () => allCodes.filter((c) => granted.has(c)).length,
    [allCodes, granted]
  );
  const modulesWithGrant = useMemo(
    () => modules.filter((m) => m.menus.some((x) => granted.has(x.permission_code))).length,
    [modules, granted]
  );

  const dirty = useMemo(() => {
    if (initial.size !== granted.size) return true;
    for (const c of granted) if (!initial.has(c)) return true;
    return false;
  }, [initial, granted]);

  const locked = isSuperAdmin;

  const toggleMenu = (code, on) => {
    if (locked) return;
    setGranted((prev) => {
      const next = new Set(prev);
      if (on) next.add(code); else next.delete(code);
      return next;
    });
  };

  const setModuleAll = (mod, on) => {
    if (locked) return;
    setGranted((prev) => {
      const next = new Set(prev);
      mod.menus.forEach((x) => { if (on) next.add(x.permission_code); else next.delete(x.permission_code); });
      return next;
    });
  };

  const moduleState = (mod) => {
    const total = mod.menus.length;
    const on = mod.menus.filter((x) => granted.has(x.permission_code)).length;
    if (on === 0) return { checked: false, indeterminate: false };
    if (on === total) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const toggleExpand = (mod) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(mod) ? next.delete(mod) : next.add(mod);
      return next;
    });

  const matches = (s) => s.toLowerCase().includes(search.trim().toLowerCase());
  const filteredModules = useMemo(() => {
    if (!search.trim()) return modules;
    return modules
      .map((m) => {
        const moduleHit = matches(m.module);
        const menus = moduleHit ? m.menus : m.menus.filter((x) => matches(x.menu));
        return { ...m, menus, _hit: moduleHit };
      })
      .filter((m) => m.menus.length > 0);
  }, [modules, search]);

  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      const grant = [...granted].filter((c) => !initial.has(c));
      const revoke = [...initial].filter((c) => !granted.has(c));
      await api.patch(`/v1/roles/${roleId}/permissions`, { grant, revoke });
      setInitial(new Set(granted));
      toast.success("Permissions updated");
      onSaved?.();
    } catch (e) {
      setSaveError(apiErrorMessage(e.response?.data?.detail) || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setGranted(new Set(initial));

  const [cloning, setCloning] = useState(false);
  const cloneFrom = async (sourceId) => {
    if (!sourceId || sourceId === roleId) return;
    setCloning(true);
    try {
      const res = await api.get(`/v1/roles/${sourceId}/permissions`);
      const src = new Set((res.data.data.menus || []).filter((m) => m.granted).map((m) => m.permission_code));
      setGranted(src);
      const name = roles.find((r) => r.id === sourceId)?.roleName || "role";
      toast.message(`Copied permissions from ${name}`, { description: "Review the changes, then click Save." });
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Failed to copy permissions");
    } finally {
      setCloning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="permission-manager">
        <div className="h-9 rounded-lg bg-muted/60 animate-pulse" />
        <SkeletonRows />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive" data-testid="pm-load-error">
        {error}
        <button onClick={load} className="ml-3 underline" data-testid="pm-retry">Retry</button>
      </div>
    );
  }

  return (
    <div data-testid="permission-manager" className="space-y-4">
      {/* Dirty banner */}
      {dirty && !locked && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 dark:bg-amber-950/30 dark:border-amber-800" data-testid="pm-dirty-banner">
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">You have unsaved changes</span>
          <div className="flex items-center gap-2">
            <button onClick={discard} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted" data-testid="pm-discard-btn">
              <RotateCcw className="h-3.5 w-3.5" /> Discard
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="pm-save-btn-banner">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Changes
            </button>
          </div>
        </div>
      )}

      {locked && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2.5 text-sm text-muted-foreground" data-testid="pm-superadmin-lock">
          <Lock className="h-4 w-4" /> SUPERADMIN always has full access — permissions are read-only for this role.
        </div>
      )}

      {saveError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="pm-error">
          {saveError}
        </div>
      )}

      {/* Search + clone */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search menus or modules…"
            data-testid="permission-search"
            className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        {!locked && roles.length > 0 && (
          <div className="relative flex items-center gap-1.5">
            <Copy className="h-4 w-4 text-muted-foreground" />
            <select
              value=""
              disabled={cloning}
              onChange={(e) => { cloneFrom(e.target.value); e.target.value = ""; }}
              data-testid="pm-clone-select"
              className="rounded-lg border border-border bg-background px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">Copy from role…</option>
              {roles.filter((r) => r.id !== roleId).map((r) => (
                <option key={r.id} value={r.id}>{r.roleName} ({r.roleCode})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Accordion */}
      <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
        {filteredModules.map((mod) => {
          const ms = moduleState(mod);
          const isOpen = !!search.trim() || expanded.has(mod.module);
          const onCount = mod.menus.filter((x) => granted.has(x.permission_code)).length;
          return (
            <div key={mod.module} className="rounded-lg border border-border bg-card" data-testid={`module-row-${slug(mod.module)}`}>
              <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                <button type="button" onClick={() => toggleExpand(mod.module)} className="flex flex-1 items-center gap-2 text-left" data-testid={`module-expand-${slug(mod.module)}`}>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  <span className="text-sm font-semibold text-foreground">{mod.module}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground" data-testid={`module-count-${slug(mod.module)}`}>
                    {onCount}/{mod.menus.length}
                  </span>
                </button>
                <div className="flex items-center gap-2">
                  <span className="hidden text-[11px] text-muted-foreground sm:inline">{ms.checked ? "All" : ms.indeterminate ? "Some" : "None"}</span>
                  <AccessToggle
                    checked={ms.checked}
                    indeterminate={ms.indeterminate}
                    disabled={locked}
                    onChange={(on) => setModuleAll(mod, on)}
                    testid={`module-toggle-${slug(mod.module)}`}
                  />
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-border">
                  {mod.menus.map((x) => {
                    const on = granted.has(x.permission_code);
                    const hit = search.trim() && (mod._hit || matches(x.menu));
                    return (
                      <div key={x.permission_code} className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${hit ? "bg-emerald-50/60 dark:bg-emerald-950/20" : ""}`} data-testid={`menu-row-${x.permission_code}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate text-[14px] text-foreground">{x.menu}</span>
                          <span className="group relative inline-flex">
                            <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
                            <span className="pointer-events-none absolute left-1/2 bottom-full z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] text-background group-hover:block" data-testid={`menu-tooltip-${x.permission_code}`}>
                              {x.description || `${x.module} → ${x.menu}`}
                            </span>
                          </span>
                          <code className="hidden text-[10px] text-muted-foreground/50 md:inline">{x.permission_code}</code>
                        </div>
                        <AccessToggle checked={on} disabled={locked} onChange={(v) => toggleMenu(x.permission_code, v)} testid={`menu-toggle-${x.permission_code}`} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {filteredModules.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="pm-no-results">No menus match “{search}”.</p>
        )}
      </div>

      {/* Legacy codes */}
      {legacyCodes.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="pm-legacy">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> Legacy / capability permission codes (read-only)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {legacyCodes.map((c) => (
              <span key={c} className="rounded-md border border-border bg-background px-2 py-0.5 font-mono text-[10px] text-muted-foreground">{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Footer: live counter + save */}
      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-border bg-card/95 pt-3 backdrop-blur">
        <p className="text-sm text-muted-foreground" data-testid="pm-counter">
          <span className="font-semibold text-foreground">{grantedCount}</span> of {totalMenus} menus granted across <span className="font-semibold text-foreground">{modulesWithGrant}</span> modules
        </p>
        {!locked && (
          <button onClick={save} disabled={saving || !dirty} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="pm-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
          </button>
        )}
      </div>
    </div>
  );
}
